<template>
  <v-list-item
    :to="props.to"
    class="kp-navigation-drawer-item"
    :value="`${props.valuePrefix}-${props.to}`"
    :active="!disableActive && isActive"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <template
      v-if="props.icon"
      #prepend
    >
      <v-icon>
        {{ props.icon }}
      </v-icon>
    </template>
    <div
      v-tooltip:top="computedTooltip"
      class="d-flex align-center justify-space-between mr-1"
    >
      <span class="text--line-height-normal text-truncate title text-body-medium">
        {{ title }}
      </span>
    </div>
    <template
      v-if="enableFavorite"
      #append
    >
      <v-btn
        v-tooltip="favoriteButtonTooltip"
        class="kp-navigation-drawer-item__icon"
        size="24"
        variant="text"
        :class="{
          'is-disabled': loading,
          'opacity-100': starIsVisible,
          'opacity-0': !starIsVisible,
        }"
        @click.prevent.stop="handleToggleFavorite"
      >
        <v-icon
          size="24px"
          :color="isFavoriteComputed ? colorFavoriteOn : colorFavoriteOff"
        >
          {{ isFavoriteComputed ? 'star' : 'star no-fill' }}
        </v-icon>
      </v-btn>
    </template>
    <template
      v-else-if="props.appendIcon"
      #append
    >
      <v-icon
        class="kp-navigation-drawer-item__icon"
        size="20"
      >
        {{ props.appendIcon }}
      </v-icon>
    </template>
  </v-list-item>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useTheme } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import useFavorites from '~/composables/useFavorites';

const props = defineProps({
  /**
   * Is the toggle favorite icon always visible?
   */
  alwaysVisible: {
    type: Boolean,
    default: false,
  },
  /**
   * Icon to the right
   */
  appendIcon: {
    type: String,
    default: '',
  },
  /**
   * Disable active state for the item
   */
  disableActive: {
    type: Boolean,
    default: false,
  },
  /**
   * Enable/disable the favorite feature for this item
   */
  enableFavorite: {
    type: Boolean,
    default: true,
  },
  /**
   * Icon to the left
   */
  icon: {
    type: String,
    default: '',
  },
  /**
   * Title
   */
  title: {
    type: String,
    required: false,
    default: '',
  },
  /**
   * The link (vue-router) to navigate to
   */
  to: {
    type: [String, Object],
    required: false,
    default: '',
  },
  /**
   * Tooltip
   */
  tooltip: {
    type: String,
    required: false,
    default: undefined,
  },
  /**
   * Prefix for the value
   * Prevents a bug from Vuetify when having multiple items with the same url in the same v-list
   */
  valuePrefix: {
    type: String,
    default: 'prefix',
  },
});

const { isFavorite, toggleFavorite } = useFavorites();
const isFavoriteComputed = computed(() => isFavorite(props.to));
const loading = ref(false);
const theme = useTheme();
const colorFavoriteOn = computed(() => theme.current.value.colors.color_favorite_on);
const colorFavoriteOff = computed(() => theme.current.value.colors.color_favorite_off);
const { t } = useI18n();
const route = useRoute();

let hoverTimeout = null;
const computedTooltip = computed(() => props.tooltip || props.title);
const isActive = computed(() => route.path === props.to);
// The "star" visibility stuff
// The visibility of the star icon on non touch devices
const showStar = ref(false);
// Is the device a touch device?
const touchDevice = ref(false);
// Compute if the star icon is visible
const starIsVisible = computed(
  () => props.alwaysVisible || touchDevice.value || showStar.value,
);
// Check if the device is a touch device
if (typeof window !== 'undefined') {
  touchDevice.value = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

/**
 * Handle mouse enter event
 */
function handleMouseEnter() {
  hoverTimeout = setTimeout(() => {
    showStar.value = true; // Show the star after 0,350 second
  }, 350);
}

/**
 * Handle mouse leave event
 */
function handleMouseLeave() {
  clearTimeout(hoverTimeout); // Clear the timeout if mouse leaves
  showStar.value = false; // Hide the star immediately
}

/**
 * Handle toggle favorite
 */
function handleToggleFavorite() {
  if (loading.value) return;
  loading.value = true;
  try {
    toggleFavorite(props.to);
  } catch (error) {
    console.error('Error while updating user favorites:', error);
  } finally {
    loading.value = false;
  }
}

/**
 * Update tooltip based on hover state
 */
const favoriteButtonTooltip = computed(() => (isFavoriteComputed.value
  ? t('navigation.drawer.favorites.removeFavorite')
  : t('navigation.drawer.favorites.addFavorite')));
</script>

<style scoped lang="scss">
.kp-navigation-drawer-item {
  &__icon {
    transition: opacity 0.2s ease, background-color 0.3s ease, box-shadow 0.3s ease;

    &.is-disabled {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.25;
    }
  }
}
</style>
